import React, { useState, useEffect, useRef } from "react";
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import CircularProgress from "@mui/material/CircularProgress";
import { api } from '../../utils/api';
import { COLORS } from "../../constants/colors";
Chart.register(ChartDataLabels);

export default function UserGenderDistributionChart({ userData }) {
    const userGenderDistributionChartRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [genderDistribution, setGenderDistribution] = useState(null);

    useEffect(() => {
        const loadGenderDistribution = async (accountId) => {
            console.log('Loading gender distribution for account:', accountId);
            try {
                if (!accountId) {
                    console.error('No accountId provided to loadGenderDistribution');
                    setError('No account ID available');
                    setLoading(false);
                    return;
                }

                // Get gender distribution from API instead of Firebase
                const genderData = await api.get(`/api/metrics/users/${accountId}/gender`);
                console.log('Gender distribution data:', genderData);

                setGenderDistribution({
                    gender_distribution: genderData.gender_distribution,
                    timestamp: new Date().toISOString()
                });
                setError(null);
            } catch (error) {
                console.error('Error fetching gender distribution:', error);
                setError(error.message || 'Failed to load gender distribution');
            } finally {
                setLoading(false);
            }
        };

        if (userData?.accountId) {
            loadGenderDistribution(userData.accountId);
        }
    }, [userData?.accountId]);

    useEffect(() => {
        if (!loading && genderDistribution && Object.keys(genderDistribution.gender_distribution).length > 0 && userGenderDistributionChartRef.current) {
            const isMobile = window.innerWidth < 640;
            
            const filteredGenderDistribution = Object.fromEntries(
                Object.entries(genderDistribution.gender_distribution).filter(
                    ([, value]) => value > 0
                )
            );
            const labels = Object.keys(filteredGenderDistribution);
            const data = Object.values(filteredGenderDistribution);
            const total = data.reduce((sum, val) => sum + val, 0);

            const chartColors = {
                'Woman': COLORS.PURPLE.hex,
                'Man': COLORS.MIDNIGHT_BLUE.hex,
                'Non-binary': COLORS.LIGHT_GRAY.hex,
                'Other': '#94A3B8',         // slate-400
                'Prefer not to say': '#CBD5E1' // slate-300
            };

            const userGenderDistributionChart = new Chart(
                userGenderDistributionChartRef.current,
                {
                    type: "doughnut",
                    data: {
                        labels: labels,
                        datasets: [
                            {
                                data: data,
                                backgroundColor: labels.map(label => chartColors[label]),
                                borderWidth: 2,
                                borderColor: '#ffffff',
                                hoverOffset: 15,
                                hoverBorderWidth: 0,
                            },
                        ],
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        cutout: '70%',
                        layout: {
                            padding: {
                                top: 0,
                                bottom: isMobile ? 10 : 20,
                            }
                        },
                        animations: {
                            animateRotate: true,
                            animateScale: true,
                            duration: 1000,
                            easing: 'easeInOutQuart'
                        },
                        plugins: {
                            legend: {
                                position: "top",
                                align: 'center',
                                labels: {
                                    padding: isMobile ? 10 : 15,
                                    usePointStyle: true,
                                    pointStyle: "circle",
                                    font: {
                                        size: isMobile ? 12 : 14,
                                        family: "'Inter', sans-serif",
                                        weight: 'bold'
                                    }
                                }
                            },
                            title: {
                                display: true,
                                text: "Gender Distribution",
                                font: {
                                    size: isMobile ? 18 : 30,
                                    family: "'Inter', sans-serif",
                                    weight: 'bold'
                                },
                                padding: { 
                                    top: isMobile ? 0 : 0,
                                    bottom: isMobile ? 8 : 15  // Reduced bottom padding to bring legend closer
                                },
                                color: '#1e293b'
                            },
                            tooltip: {
                                enabled: true,
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                titleColor: '#1e293b',
                                bodyColor: '#1e293b',
                                borderColor: '#e2e8f0',
                                borderWidth: 1,
                                padding: isMobile ? 8 : 12,
                                titleFont: {
                                    size: isMobile ? 12 : 14,
                                    weight: 'bold',
                                    family: "'Inter', sans-serif"
                                },
                                bodyFont: {
                                    size: isMobile ? 11 : 13,
                                    weight: 'bold',
                                    family: "'Inter', sans-serif"
                                },
                                callbacks: {
                                    label: (context) => {
                                        const value = context.raw;
                                        const percentage = ((value / total) * 100).toFixed(1);
                                        return `${context.label}: ${value} (${percentage}%)`;
                                    },
                                },
                            },
                            datalabels: {
                                display: false
                            },
                        },
                        hover: {
                            mode: 'nearest',
                            intersect: true,
                            animationDuration: 150
                        },
                        interaction: {
                            mode: 'nearest',
                            intersect: true,
                        }
                    },
                }
            );

            return () => userGenderDistributionChart.destroy();
        }
    }, [genderDistribution, loading]);

    if (loading) {
        return (
            <div
                style={{
                    padding: "24px",
                    backgroundColor: "white",
                    borderRadius: "8px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                    margin: "20px",
                    textAlign: "center",
                }}
            >
                <CircularProgress />
            </div>
        );
    }

    if (error) {
        return (
            <div
                style={{
                    padding: "24px",
                    backgroundColor: "white",
                    borderRadius: "8px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                    margin: "20px",
                    color: "red",
                    textAlign: "center",
                }}
            >
                Error loading users: {error}
            </div>
        );
    }

    return (
        <div style={{ width: "100%", height: "400px" }}>
            <canvas ref={userGenderDistributionChartRef}></canvas>
        </div>
    );
}

